import { CrackAnalysis, Finding, Severity } from "../types";

const severityColors: Record<Severity, string> = {
  [Severity.NONE]: '#22c55e',
  [Severity.LOW]: '#0ea5e9',
  [Severity.MEDIUM]: '#eab308',
  [Severity.HIGH]: '#f97316',
  [Severity.CRITICAL]: '#ef4444',
};

const formatTime = (seconds: number) => {
  const totalSecs = Math.round(seconds);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const drawDefectBox = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string, label: string) => {
  ctx.fillStyle = color + '1a';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = color;
  ctx.font = 'bold 16px monospace';
  const textMetrics = ctx.measureText(label);
  const textWidth = textMetrics.width;
  ctx.fillRect(x, y - 26, textWidth + 14, 26);
  ctx.fillStyle = '#0a1120';
  ctx.fillText(label, x + 7, y - 8);
};

/**
 * Renders a single state of the audit onto the provided canvas context.
 */
const renderFrameContent = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
  frame: CrackAnalysis,
  index: number,
  total: number
) => {
  const hasDefects = frame.findings && frame.findings.length > 0;

  // 1. Clear and Draw Base Image
  ctx.fillStyle = '#0a1120';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Center and scale image to fit canvas
  const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
  const x = (canvas.width / 2) - (img.width / 2) * scale;
  const y = (canvas.height / 2) - (img.height / 2) * scale;

  ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

  // 2. Draw Findings
  if (frame.findings) {
    frame.findings.forEach((finding, fIdx) => {
      if (!finding.box) return;
      const [ymin, xmin, ymax, xmax] = finding.box;

      // Map box coordinates (0-1000) to actual image placement on canvas
      const bx = x + (xmin / 1000) * (img.width * scale);
      const by = y + (ymin / 1000) * (img.height * scale);
      const bw = ((xmax - xmin) / 1000) * (img.width * scale);
      const bh = ((ymax - ymin) / 1000) * (img.height * scale);

      drawDefectBox(
        ctx,
        bx, by, bw, bh,
        severityColors[finding.severity],
        `#${fIdx + 1} ${finding.type.toUpperCase()}`
      );
    });
  }

  // 3. Status Bar Overlay
  const footerHeight = 80;
  ctx.fillStyle = 'rgba(0,0,0,0.9)';
  ctx.fillRect(0, canvas.height - footerHeight, canvas.width, footerHeight);

  // HUD Elements
  ctx.fillStyle = hasDefects ? '#f87171' : '#06b6d4';
  ctx.font = 'bold 24px monospace';
  const statusText = hasDefects ? `!!! ANOMALY DETECTED !!!` : `SURFACE NOMINAL`;
  ctx.fillText(`V6.0 AUDIT | TIME: ${formatTime(frame.timestamp || 0)} | ${statusText}`, 30, canvas.height - 45);

  // Secondary Metadata Line
  ctx.fillStyle = '#cbd5e1';
  ctx.font = '18px monospace';

  let metaText = `SECTOR: ${index + 1}/${total} | MODE: ${frame.mode || 'GENERAL'}`;
  if (hasDefects && frame.findings) {
    const topFinding = frame.findings[0];
    metaText += ` | PRIORITY: ${topFinding.type} (${topFinding.severity})`;
    if (frame.confidenceScore) metaText += ` | CONF: ${(frame.confidenceScore * 100).toFixed(0)}%`;
  }

  ctx.fillText(metaText, 30, canvas.height - 15);

  ctx.fillStyle = '#94a3b8';
  ctx.textAlign = 'right';
  ctx.fillText(`NEURAL_LINK: ACTIVE`, canvas.width - 30, canvas.height - 45);
  ctx.font = '14px monospace';
  ctx.fillText(new Date().toISOString().split('T')[0], canvas.width - 30, canvas.height - 15);
  ctx.textAlign = 'left';

  // Animated Scan Line (Visual flourish to ensure encoder sees movement)
  const scanY = (Date.now() % 2000 / 2000) * canvas.height;
  ctx.strokeStyle = hasDefects ? 'rgba(248, 113, 113, 0.2)' : 'rgba(6, 182, 212, 0.2)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, scanY);
  ctx.lineTo(canvas.width, scanY);
  ctx.stroke();
};

export const generateAnnotatedVideo = async (
  timeline: (CrackAnalysis & { findings?: Finding[] })[],
  onProgress: (progress: number) => void
): Promise<{ url: string; extension: string }> => {
  return new Promise(async (resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) throw new Error("Canvas context initialization failed.");

      canvas.width = 1280;
      canvas.height = 720;

      const stream = canvas.captureStream(30);

      // Determine supported mime type, prioritizing MP4
      const types = [
        'video/mp4',
        'video/webm;codecs=h264',
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm'
      ];
      const mimeType = types.find(t => MediaRecorder.isTypeSupported(t)) || 'video/webm';
      console.log(`Recording using MIME type: ${mimeType}`);

      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 5000000 // 5Mbps for high fidelity
      });

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
        resolve({ url: URL.createObjectURL(blob), extension });
      };

      recorder.start();

      const totalEntries = timeline.length;
      const fps = 30;
      const defectHoldSeconds = 5.0; // 5 Seconds per defect
      const nominalHoldSeconds = 0.2; // Quick scan for nominal frames

      for (let i = 0; i < totalEntries; i++) {
        const frame = timeline[i];
        onProgress(Math.round((i / totalEntries) * 100));

        // Load image for this step
        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise((res) => {
          img.onload = res;
          img.onerror = () => { console.warn("Skipping frame, load failed"); res(null); };
          img.src = frame.frameImage || '';
        });

        if (!img.complete || img.naturalWidth === 0) continue;

        const hasDefects = frame.findings && frame.findings.length > 0;
        const holdTime = hasDefects ? defectHoldSeconds : nominalHoldSeconds;
        const frameCount = Math.max(1, Math.floor(holdTime * fps));

        // Render loop for the duration of this frame
        for (let j = 0; j < frameCount; j++) {
          renderFrameContent(ctx, canvas, img, frame, i, totalEntries);
          // Wait for one frame duration (~33ms) to throttle draw calls
          await new Promise(r => setTimeout(r, 33));
        }
      }

      // Add a small buffer at the end
      await new Promise(r => setTimeout(r, 1000));
      recorder.stop();

    } catch (err) {
      console.error("Video Generation Error:", err);
      reject(err);
    }
  });
};